/* eslint-disable @typescript-eslint/no-magic-numbers */
import * as d3 from 'd3';
import { DateTime } from 'luxon';

import { findSmallestDifferenceBetweenTwoNumbers } from '@shared/helpers/graph/calculation.helper';
import { D3ChartSvgg, D3CursorEvent, D3XScale } from '@shared/types/common-graph.types';
import { ValueKeys } from '@shared/types/value-keys';
import { formatTime } from '@shared/utils/time';

import { ChartCompactDataPointLayout } from '../chart.view-model';

export const getXScaleMinMaxValues = (xAxisDataSet: number[], edgePaddingRatio?: number): [number, number] => {
  const xAxisDataSetMinMax = d3.extent(xAxisDataSet) as [number, number];

  const padding =
    edgePaddingRatio !== undefined
      ? findSmallestDifferenceBetweenTwoNumbers(xAxisDataSet) * edgePaddingRatio
      : (xAxisDataSetMinMax[1] - xAxisDataSetMinMax[0]) * 0.025;

  return [xAxisDataSetMinMax[0] - padding, xAxisDataSetMinMax[1] + padding];
};

export const createXScale = (minXValue: number, maxXValue: number): D3XScale => {
  return d3.scaleTime().domain([new Date(minXValue), new Date(maxXValue)]);
};

export const getXAxisBandwidth = (xAxisDataSet: number[], minX: number, maxX: number, width: number): number => {
  const step = findSmallestDifferenceBetweenTwoNumbers(xAxisDataSet);
  return (width / (maxX - minX)) * step;
};

export const getCompactDataPointLayout = (
  dataPointCount: number,
  availableChartsAreaWidth: number,
  compactDataPointLayout: ChartCompactDataPointLayout | undefined
): { bandwidth: number; start: number; end: number } | undefined => {
  if (!compactDataPointLayout || dataPointCount === 0) {
    return undefined;
  }

  const requestedWidth = dataPointCount * compactDataPointLayout.barWidth + (dataPointCount - 1) * compactDataPointLayout.gap;
  const scale = Math.min(1, availableChartsAreaWidth / requestedWidth);
  const barWidth = compactDataPointLayout.barWidth * scale;
  const bandwidth = (compactDataPointLayout.barWidth + compactDataPointLayout.gap) * scale;

  return {
    bandwidth,
    start: barWidth / 2,
    end: barWidth / 2 + (dataPointCount - 1) * bandwidth,
  };
};

export const getClosestIndexToCursor = <Data extends object>(
  event: D3CursorEvent,
  data: Data[],
  dataKey: ValueKeys<Data, number | DateTime>,
  xScale: D3XScale,
  linesContainer: D3ChartSvgg
): number | null => {
  const mtuUnderCursor = xScale.invert(d3.pointer(event, linesContainer.node())[0]);

  let closestIndex = d3.bisectCenter(
    data.map((d) => {
      const mtu = d[dataKey] as DateTime | number;
      return typeof mtu === 'number' ? new Date(mtu) : mtu.toJSDate();
    }),
    mtuUnderCursor
  );
  if (closestIndex < 0) {
    closestIndex = 0;
  }

  return closestIndex;
};

export const getTickValueText = (d: number, xAxisDataSet: number[], verticalTexts: boolean, timeZone?: string): string => {
  const dateTime = DateTime.fromMillis(d, timeZone ? { zone: timeZone } : undefined);

  if (!verticalTexts) {
    return dateTime.toFormat('ccc');
  }

  return `${dateTime.toFormat('dd/MM')} ${formatTime(d, timeZone)}`;
};
